import { CriticalDataProduct } from '@components/SearchBars/SearchBarExpansiveModel/SearchBarExpansiveModel';
import { ITag, ITagLimitWithDesc } from '@models/Tag.model';
import { ProductCriticalData } from '@services/queries/products/getProductModels/getProductModels';
import {
  IKnownTag,
  KnownProducts,
  KnownProductType,
} from '@store/redux/features/inventory/knownData';
import { cx } from '@utils/cx';
import { sortStr } from '@utils/sorting/non-ascii';
import extend from 'just-extend';
import { FieldPath, FieldPathValue, PathValue } from 'react-hook-form';
import get from 'just-safe-get';
import set from 'just-safe-set';
import { filterGlobalDirty } from '../../helpers/filterDirty';
import { filterDirtySingle } from '../../helpers/filterDirtySingle';
import { ConfigSingle, ConfigGroup } from '../../helpers/getInputs';
import { ChildrenT, customFieldIdPrefix } from '../../helpers/HeadingsCalc';
import { IFormDataOnboardingModal } from '../../OnBoardingModal';
import {
  EquipmentTypeKeepNulls,
  GlobalInput,
  GlobalEquipment,
} from '../../types';
import { NOT_APPLICABLE } from './constants';
import {
  FieldValues,
  PropsShared,
  FlattenSingle,
  FlattenGlobal,
} from './types';

/* Identification */
export function getSelectedValue(
  productId: string | null | undefined,
  knownProducts: KnownProducts,
) {
  const globalProduct = knownProducts[productId ?? ''];
  let selectedValue: string | null = null;
  if (globalProduct) {
    selectedValue = getModelSearchValue(globalProduct);
  }
  return selectedValue;
}
export function mapProductModels(
  item: ProductCriticalData,
): CriticalDataProduct {
  const value = {
    content: item.model,
    id: item.id,
    manufacturerName: item.manufacturerName,
    imageUrl: item?.imageUrl,
  };
  return value;
}

const DEFAULT_MODEL = 'Unknown';
const DEFAULT_MANUFACTURER = 'Unknown';
export function getModelSearchValue(globalProduct: KnownProductType) {
  const { model = DEFAULT_MODEL, manufacturerName = DEFAULT_MANUFACTURER } =
    globalProduct;

  return `${model} (${manufacturerName}) `;
}
export function criticalToKnown(
  critical: CriticalDataProduct,
): KnownProductType {
  const { id, ...rest } = critical;
  return {
    id,
    model: rest.content,
    imageUrl: rest.imageUrl ?? null,
    manufacturerName: rest.manufacturerName ?? null,
    manufacturerImg: null,
  };
}

// Firmware
export function showCheckbox(
  installedFirmwareVersion: string | null | undefined,
): boolean {
  return (
    installedFirmwareVersion === NOT_APPLICABLE ||
    // To deal with existing ""
    installedFirmwareVersion === '' ||
    installedFirmwareVersion === null ||
    installedFirmwareVersion === undefined
  );
}
// New Api
export function setForAllNew<
  TFieldName extends FieldPath<FieldValues>,
  TFieldValue extends PathValue<FieldValues, TFieldName>,
>(
  path: TFieldName,
  value: TFieldValue | Identity<TFieldValue>,
  setFormV: PropsShared['setFormV'],
) {
  setFormV(previous => {
    const { equipments, dirtyFields } = previous;
    const cloneDirtyFields = { ...dirtyFields };
    if (!cloneDirtyFields.equipments) {
      cloneDirtyFields.equipments = [];
    }
    const cloneEquipments = [...equipments];
    const clonePrevious = {
      ...previous,
      equipments: cloneEquipments,
      dirtyFields: cloneDirtyFields,
    };
    cloneEquipments.forEach((equipment, index) => {
      let _value: TFieldValue = value as TFieldValue;
      if (value instanceof Function) {
        _value = value(get(equipment, path)) as TFieldValue;
      }
      set(equipment, path, _value);
      set(cloneDirtyFields.equipments as [], `${index}.${path}`, true);
    });
    return { ...clonePrevious, isDirty: true };
  });
}

//  Custom fields

export function extractFieldId(idWithPrefix: ChildrenT): string {
  const id = idWithPrefix.replace(customFieldIdPrefix, '');
  return id;
}

// Tags
export function handleGlobalDeleteNew(
  tag: Pick<ITag, 'id'>,
  setGlobalFormV: PropsShared['setGlobalFormV'],
  setFormV: PropsShared['setFormV'],
) {
  return () => {
    // A delete does not unsort values.
    onChangeGlobal(
      'tagsId',
      prev => {
        const tagCount = { ...prev };
        delete tagCount[tag.id];
        return tagCount;
      },
      setGlobalFormV,
    );

    setForAllNew(
      'tagsId',
      value => value.filter(id => id !== tag.id),
      setFormV,
    );
  };
}
export const calcTagProps = () => ({
  className: 'rm-m',
  showDelete: undefined,
});
export const disabledTagProps = () => ({
  ...calcTagProps(),
  className: cx(calcTagProps().className, 'rm-cur'),
  showDelete: false,
});
export function addTagsTo(
  newTags: ITagLimitWithDesc[],
  tagsId: string[],
  knownTags: IKnownTag,
) {
  // add
  const actuallyNewTags = newTags.filter(tag => !tagsId.includes(tag.id));
  const oldTagsWithNames = tagsId.map(id => knownTags[id]);
  const _newTags = oldTagsWithNames.concat(...actuallyNewTags);
  // Sort.
  const newTagsWithNames = _newTags.sort((taga, tagb) =>
    sortStr(taga.name, tagb.name),
  );
  // set
  const newTagsId = newTagsWithNames.map(tag => tag.id);
  return newTagsId;
}

export function handleSingleDeleteNew(
  tag: Pick<ITag, 'id'>,
  index: number,
  tagsIdValue: string[],
  setGlobalFormV: PropsShared['setGlobalFormV'],
  setFormV: PropsShared['setFormV'],
) {
  return () => {
    // Delete does not undo sort
    const newTags = tagsIdValue.filter(id => id !== tag.id);

    onChangeSingle(`equipments.${index}.tagsId`, newTags, setFormV);
    // Update list
    onChangeGlobal(
      'tagsId',
      previous => {
        const oldtags = { ...previous };
        if (oldtags[tag.id].count === 1) {
          delete oldtags[tag.id];
        } else {
          set(oldtags, `${tag.id}.count`, oldtags[tag.id].count - 1);
        }
        return oldtags;
      },
      setGlobalFormV,
    );
  };
}
// Inputs
/**
 * Flattens inputs props to atomic values to trigger state update.
 */
export function getInputProps<
  TPostFixes extends FieldPath<EquipmentTypeKeepNulls>,
>(
  postFixesToGet: TPostFixes[] | readonly TPostFixes[],
  formV: ConfigSingle['formV'],
  index: number,
): FlattenSingle<TPostFixes> {
  // This must pass.

  const merged = {} as FlattenSingle<TPostFixes>;
  postFixesToGet.forEach(path => {
    const value = get(formV, `equipments.${index}.${path}`);
    merged[path] = value;
  });
  return merged;
}
// These two are the same.
export function getGlobalInputProps<
  TPostFixes extends FieldPath<GlobalEquipment>,
>(
  postFixesToGet: TPostFixes[] | readonly TPostFixes[],
  formV: ConfigGroup['globalFormV'],
): FlattenGlobal<TPostFixes> {
  const merged: FlattenGlobal<TPostFixes> = {} as FlattenGlobal<TPostFixes>;
  postFixesToGet.forEach(path => {
    const value = get(formV, `equipment.${path}`);
    merged[path] = value;
  });
  return merged;
}

type Identity<T> = (v: T) => T;
/**
 * OnChange fn with correct values. Nb, types are different from setValue due to ts limits.
 */
export function onChangeSingle<IFieldPath extends FieldPath<FieldValues>>(
  path: `equipments.${number}.${IFieldPath}`,
  value:
    | FieldPathValue<FieldValues, IFieldPath>
    | Identity<FieldPathValue<FieldValues, IFieldPath>>,
  setFormV: PropsShared['setFormV'],
): void {
  setFormV(previous => {
    //  CLone.
    const { clonePrevious, cloneDirtyFields } = cloneSingle(previous);
    //  Set
    let _value = value;
    if (value instanceof Function) {
      _value = value(get(clonePrevious, path));
    }
    if (!cloneDirtyFields.equipments) {
      cloneDirtyFields.equipments = [];
    }
    set(clonePrevious, path, _value);
    set(cloneDirtyFields, path, true);
    return {
      ...clonePrevious,
      isDirty: true,
    };
  });
}
function cloneSingle(previous: IFormDataOnboardingModal) {
  const { equipments, dirtyFields } = previous;
  const cloneEquipments = [...equipments];
  const cloneDirtyFields = { ...dirtyFields };
  const clonePrevious = {
    ...previous,
    equipments: cloneEquipments,
    dirtyFields: cloneDirtyFields,
  };
  return { clonePrevious, cloneDirtyFields };
}

export function onChangeGlobal<IFieldPath extends FieldPath<GlobalEquipment>>(
  path: IFieldPath,
  value:
    | FieldPathValue<GlobalEquipment, IFieldPath>
    | Identity<FieldPathValue<GlobalEquipment, IFieldPath>>,
  setGlobalFormV: PropsShared['setGlobalFormV'],
): void {
  setGlobalFormV(previous => {
    //  CLone.
    const { previousClone, dirtyGlobalFieldsClone } = cloneGlobal(previous);
    const { equipment } = previousClone;
    //  Set
    let _value = value;
    if (value instanceof Function) {
      _value = value(get(equipment, path));
    }
    if (!dirtyGlobalFieldsClone.equipment) {
      dirtyGlobalFieldsClone.equipment = {};
    }
    set(equipment, path, _value);
    set(dirtyGlobalFieldsClone.equipment, path, true);
    return {
      ...previousClone,
      isDirty: true,
    };
  });
}
function cloneGlobal(previous: GlobalInput) {
  const { equipment, dirtyGlobalFields } = previous;
  const equipmentClone = { ...equipment };
  const dirtyGlobalFieldsClone = { ...dirtyGlobalFields };
  const previousClone = {
    ...previous,
    equipment: equipmentClone,
    dirtyGlobalFields: dirtyGlobalFieldsClone,
  };
  return { previousClone, dirtyGlobalFieldsClone };
}

/**
 * Reset inputs.
 */
export function reset(
  defaultValues: IFormDataOnboardingModal['equipments'],
  values: IFormDataOnboardingModal['equipments'],
  dirtyFields: IFormDataOnboardingModal['dirtyFields'],
): IFormDataOnboardingModal['equipments'] {
  const newEquipments = defaultValues.map((defaultValue, index) => {
    const dirtyField = dirtyFields?.equipments?.at(index);
    const dirtySingle = filterDirtySingle(values[index], dirtyField);
    const merged = extend(
      true,
      defaultValue,
      dirtySingle || {},
    ) as EquipmentTypeKeepNulls;
    return merged;
  });
  return newEquipments;
}
/**
 * Reset inputs.
 */
export function resetGlobal(
  defaultValues: GlobalInput['equipment'],
  values: GlobalInput['equipment'],
  dirtyFields: GlobalInput['dirtyGlobalFields'],
): GlobalInput['equipment'] {
  const dirtyField = dirtyFields?.equipment;
  const dirty = filterGlobalDirty(values, dirtyField);
  const merged = extend(true, defaultValues, dirty || {}) as GlobalEquipment;
  return merged;
}
